/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * طبقة اتصال بالـ backend الحقيقي (Express + MySQL). توكن الجلسة (JWT) بيتخزن في
 * httpOnly cookie بيضبطها السيرفر مباشرة وتظل مسار الجلسة الأساسي. عند استضافة الواجهة
 * والـ API على نطاقين مختلفين فقط، نستخدم Bearer محفوظاً في localStorage كمسار توافق
 * لأن Safari/iOS قد يحجب كوكي SameSite=None بالكامل. كل الطلبات تظل تُرسل أيضاً مع
 * credentials: 'include' كي تستخدم المتصفحات الكوكي الأكثر أماناً متى كانت متاحة.
 */

// عنوان السيرفر (Backend) لما يكون مستضافاً بشكل منفصل عن الفرونت اند
// (مثال: فرونت على Hostinger + باك اند على Render). اتركه فارغاً لو الفرونت
// والباك اند على نفس الدومين (نفس السيرفر بيقدّم الاتنين).
// يُضبط وقت الـ build عبر متغير VITE_API_BASE_URL (بدون / في الآخر)، مثال:
// VITE_API_BASE_URL=https://istihdaf-api.onrender.com
const API_BASE_URL = (import.meta.env?.VITE_API_BASE_URL || '').replace(/\/$/, '');
const API_ORIGIN = (() => {
  try { return API_BASE_URL ? new URL(API_BASE_URL).origin : ''; } catch { return ''; }
})();
const CROSS_ORIGIN_AUTH = typeof window !== 'undefined' && Boolean(API_ORIGIN) && API_ORIGIN !== window.location.origin;
const AUTH_COMPAT_STORAGE_KEY = 'istihdaf_cross_origin_session';

function readCompatibilityToken(): string {
  if (!CROSS_ORIGIN_AUTH) return '';
  try { return window.localStorage.getItem(AUTH_COMPAT_STORAGE_KEY) || ''; } catch { return ''; }
}

function saveCompatibilityToken(token: unknown): void {
  if (!CROSS_ORIGIN_AUTH || typeof token !== 'string' || !token) return;
  try { window.localStorage.setItem(AUTH_COMPAT_STORAGE_KEY, token); } catch { /* cookie may still work */ }
}

function clearCompatibilityToken(): void {
  try { window.localStorage.removeItem(AUTH_COMPAT_STORAGE_KEY); } catch { /* no-op */ }
}
const PUBLIC_ASSET_ORIGIN = String(
  import.meta.env?.VITE_PUBLIC_SITE_URL
  || (import.meta.env?.PROD ? 'https://istihdaf.site' : '')
).replace(/\/+$/, '');

// الصور لها نطاق مستقل عن API: المسارات النسبية وأي روابط uploads قديمة تابعة
// للباك اند تُعرض من الدومين العام للموقع، بينما تظل طلبات JSON على API_BASE_URL.
export function resolveAssetUrlAgainst(path: string | null | undefined, publicOrigin: string, apiOrigin = ''): string {
  if (!path) return '';
  if (/^(data:|blob:)/i.test(path)) return path;
  const publicAssetUrl = (pathname: string, search = '', hash = '') => `${publicOrigin.replace(/\/+$/, '')}${pathname.startsWith('/') ? pathname : `/${pathname}`}${search}${hash}`;
  if (/^https?:/i.test(path)) {
    try {
      const parsed = new URL(path);
      const isBackendAsset = parsed.hostname.toLowerCase() === 'istihdaf.runasp.net'
        || (apiOrigin && parsed.origin === apiOrigin && parsed.pathname.startsWith('/uploads/'));
      return isBackendAsset ? publicAssetUrl(parsed.pathname, parsed.search, parsed.hash) : path;
    } catch {
      return path;
    }
  }
  return publicAssetUrl(path);
}

export function resolveAssetUrl(path: string | null | undefined): string {
  return resolveAssetUrlAgainst(path, PUBLIC_ASSET_ORIGIN, API_ORIGIN);
}

// ============================================================
// معالج 401 عام على مستوى التطبيق كله. المشكلة اللي بيحلّها: أي جلسة تتبطّل
// أثناء الاستخدام (تغيير كلمة المرور من جهاز تاني، تسجيل خروج عن بُعد، انتهاء
// التوكن...إلخ) كانت بتخلّي كل نداء API شغال في الخلفية (لوحة الأدمن بتبعت
// حوالي 12+ نداء متوازي عند فتحها مثلاً) يفشل بصمت بـ 401 ويتسجّل في الـ
// console بس، من غير ما الواجهة تتفاعل — فيفضل المستخدم شايف لوحة فاضية/معطوبة
// من غير ما يعرف إنه لازم يسجّل دخول تاني. الحل: أي مكوّن (App.tsx) يسجّل
// callback هنا مرة واحدة، وأول 401 "حقيقي" (مش من نموذج تسجيل الدخول نفسه لو
// كلمة السر غلط) بيشغّله فيرجّع المستخدم لشاشة الدخول فوراً. بنستخدم فلاج
// unauthorizedNotified عشان لو كذا نداء اتبعتوا مع بعض ورجعوا 401 مع بعض،
// الـ callback يتشغّل مرة واحدة بس مش مرة لكل نداء فاشل.
const PRE_AUTH_PATHS = new Set([
  '/api/auth/login',
  '/api/auth/google',
  '/api/auth/register',
  '/api/auth/verify-email',
  '/api/auth/resend-verification',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  '/api/auth/verify-2fa',
  '/api/auth/resend-2fa',
  '/api/auth/logout',
]);
const CREDENTIAL_CONFIRMATION_REQUESTS = new Set([
  'POST /api/users/me/change-email',
  'POST /api/users/me/change-password',
  'PUT /api/users/me/two-factor',
  'DELETE /api/users/me',
]);

let unauthorizedHandler: (() => void) | null = null;
let unauthorizedNotified = false;

/** بيسجّل الدالة اللي هتتشغّل أول مرة أي نداء (غير نداءات تسجيل الدخول نفسها)
 *  يرجع 401 — عادةً بترجّع المستخدم لشاشة الدخول وتعرض تنبيه "انتهت الجلسة". */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/** بيصفّر فلاج التنبيه بعد نجاح تسجيل الدخول (أو التحقق من الجلسة عند فتح
 *  الموقع) عشان لو الجلسة اتبطّلت تاني بعد كده، التنبيه يشتغل من جديد. */
export function notifyAuthenticated(): void {
  unauthorizedNotified = false;
}

async function request<T = any>(path: string, options: RequestInit & { silentAuth?: boolean } = {}): Promise<T> {
  const { silentAuth, ...fetchOptions } = options;
  const headers: Record<string, string> = { ...((fetchOptions.headers as Record<string, string>) || {}) };
  // FormData needs the boundary generated by the browser; setting JSON here would
  // make multipart image uploads unreadable by multer on the API server.
  // لا نضيف Content-Type لطلبات GET الخالية من body؛ إضافته كان يحوّل كل قراءة بسيطة
  // عبر نطاق مختلف إلى CORS preflight غير ضروري ويزيد فشل الشبكات البطيئة على الهاتف.
  if (fetchOptions.body && !(fetchOptions.body instanceof FormData)) headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  const compatibilityToken = readCompatibilityToken();
  if (compatibilityToken && !headers.Authorization) headers.Authorization = `Bearer ${compatibilityToken}`;
  const requestKey = `${String(fetchOptions.method || 'GET').toUpperCase()} ${path}`;
  const isCredentialConfirmation = CREDENTIAL_CONFIRMATION_REQUESTS.has(requestKey);

  const res = await fetch(`${API_BASE_URL}${path}`, { ...fetchOptions, headers, credentials: 'include' });
  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const requiresCurrentBackend = path === '/api/content-library'
      || path.startsWith('/api/content-library/')
      || path === '/api/integrations'
      || path.startsWith('/api/integrations/');
    const backendUpdateRequired = res.status === 404 && requiresCurrentBackend;
    const err: any = new Error(
      backendUpdateRequired
        ? 'نسخة الخادم المنشورة قديمة ولا تحتوي هذه الميزة. ارفع حزمة Backend الجديدة ثم أعد تشغيل التطبيق.'
        : (data.error || 'حدث خطأ في الاتصال بالخادم'),
    );
    err.status = res.status;
    err.code = backendUpdateRequired ? 'BACKEND_UPDATE_REQUIRED' : data.code;
    if (data.needsVerification) err.needsVerification = true;
    if (data.email) err.email = data.email;

    // silentAuth: نداءات زي فحص الجلسة عند فتح الموقع (api.me عند الإقلاع) — 401 هنا
    // معناه "زائر مش مسجّل دخول" وهو وضع طبيعي متوقع، مش انقطاع جلسة فعلي، فمفيش داعي
    // لإطلاق تنبيه "انتهت صلاحية الجلسة" لزائر أصلاً مادخلش. الـ catch بتاع الاستدعاء
    // نفسه بيتكفّل بإظهار شاشة تسجيل الدخول عادي.
    // A 401 from an in-account password confirmation means the entered password was
    // wrong, not that the session token expired. Logging out here was a separate source
    // of apparently random account exits from the profile page.
    if (res.status === 401 && compatibilityToken && !isCredentialConfirmation) clearCompatibilityToken();
    if (res.status === 401 && !isCredentialConfirmation && !PRE_AUTH_PATHS.has(path) && !silentAuth && !unauthorizedNotified) {
      unauthorizedNotified = true;
      unauthorizedHandler?.();
    }

    throw err;
  }
  if (data?.sessionToken) saveCompatibilityToken(data.sessionToken);
  return data;
}

export interface CreditOperationPreview {
  operation_key: string;
  label_ar: string;
  label_en: string;
  cost_credits: number;
  balance_before: number;
  balance_after: number;
  campaigns_remaining: number | null;
}

type CreditConfirmationHandler = (operation: CreditOperationPreview) => Promise<boolean>;
let creditConfirmationHandler: CreditConfirmationHandler | null = null;

export function setCreditConfirmationHandler(handler: CreditConfirmationHandler | null): void {
  creditConfirmationHandler = handler;
}

async function confirmCreditOperation(operationKey: string): Promise<void> {
  const preview: any = await request('/api/users/me/credit-costs');
  const operation = (preview.operations || []).find((item: any) => item.operation_key === operationKey);
  const cost = Math.max(0, Number(operation?.cost_credits || 0));
  if (!operation || cost === 0) return;
  const campaignOperation = (preview.operations || []).find((item: any) => item.operation_key === 'campaign_generation');
  const campaignCost = Math.max(0, Number(campaignOperation?.cost_credits || 0));
  const confirmation: CreditOperationPreview = {
    ...operation,
    cost_credits: cost,
    balance_before: Math.max(0, Number(preview.balance || 0)),
    balance_after: Math.max(0, Number(operation.balance_after || 0)),
    campaigns_remaining: campaignCost > 0 ? Math.floor(Math.max(0, Number(operation.balance_after || 0)) / campaignCost) : null,
  };
  const accepted = creditConfirmationHandler
    ? await creditConfirmationHandler(confirmation)
    : window.confirm(`هذه العملية ستستهلك ${cost} كريدت، وسيصبح رصيدك ${confirmation.balance_after}.\nThis action costs ${cost} credits. Continue?`);
  if (!accepted) { const error: any = new Error('تم إلغاء العملية'); error.code = 'USER_CANCELLED'; throw error; }
}

async function creditRequest(path: string, operationKey: string, options: RequestInit): Promise<any> {
  await confirmCreditOperation(operationKey);
  return request(path, options);
}

export const api = {
  googleConfig: () => request<{ enabled: boolean; clientId: string | null }>('/api/auth/google/config'),

  register: (payload: { name: string; email: string; phone: string; password: string }) =>
    request('/api/auth/register', { method: 'POST', body: JSON.stringify(payload) }),

  login: (payload: { email: string; password: string }) =>
    request('/api/auth/login', { method: 'POST', body: JSON.stringify(payload) }),

  googleLogin: (credential: string) =>
    request('/api/auth/google', { method: 'POST', body: JSON.stringify({ credential }) }),

  verifyEmail: (payload: { email: string; code: string }) =>
    request('/api/auth/verify-email', { method: 'POST', body: JSON.stringify(payload) }),

  resendVerification: (email: string) =>
    request('/api/auth/resend-verification', { method: 'POST', body: JSON.stringify({ email }) }),

  forgotPassword: (email: string) =>
    request('/api/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),

  resetPassword: (payload: { email: string; code: string; newPassword: string }) =>
    request('/api/auth/reset-password', { method: 'POST', body: JSON.stringify(payload) }),

  verify2FA: (payload: { email: string; code: string; challengeId: string }) =>
    request('/api/auth/verify-2fa', { method: 'POST', body: JSON.stringify(payload) }),

  resend2FA: (email: string, challengeId: string) =>
    request('/api/auth/resend-2fa', { method: 'POST', body: JSON.stringify({ email, challengeId }) }),

  me: (opts?: { silent?: boolean }) => request('/api/auth/me', { silentAuth: opts?.silent }),

  logout: async () => {
    try { return await request('/api/auth/logout', { method: 'POST' }); }
    finally { clearCompatibilityToken(); }
  },

  consumeCredit: () => request('/api/users/me/consume-credit', { method: 'POST' }),

  getCreditHistory: (limit = 30) => request(`/api/users/me/credit-history?limit=${Math.max(1, Math.min(100, limit))}`),

  getCreditForecast: () => request('/api/users/me/credit-forecast'),

  getCreditSummary: () => request('/api/users/me/credit-summary'),

  startTrial: () => request('/api/users/me/start-trial', { method: 'POST' }),

  getNotifications: () => request('/api/users/me/notifications'),
  markNotificationRead: (id: string) => request(`/api/users/me/notifications/${encodeURIComponent(id)}/read`, { method: 'PATCH' }),
  markAllNotificationsRead: () => request('/api/users/me/notifications/read-all', { method: 'POST' }),
  getCreditCosts: () => request('/api/users/me/credit-costs'),
  getCreditPackages: () => request('/api/credit-packages'),
  createCreditPackageOrder: (packageId: string) => request('/api/credit-package-orders', { method: 'POST', body: JSON.stringify({ packageId }) }),
  getCreditPackageOrders: () => request('/api/users/me/credit-package-orders'),
  redeemPromotion: (code: string) => request('/api/users/me/redeem-promotion', { method: 'POST', body: JSON.stringify({ code }) }),

  getContentLibrary: () => request('/api/content-library'),

  saveContentLibraryItem: (payload: { industry: string; category: string; title: string; content: string; sourceCampaignId?: string }) =>
    request('/api/content-library', { method: 'POST', body: JSON.stringify(payload) }),

  deleteContentLibraryItem: (id: string) => request(`/api/content-library/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // ── الملف الشخصي ──
  updateProfile: (payload: { name?: string; dateOfBirth?: string | null }) =>
    request('/api/users/me', { method: 'PATCH', body: JSON.stringify(payload) }),

  updateAvatar: (avatarBase64: string | null) =>
    request('/api/users/me/avatar', { method: 'PUT', body: JSON.stringify({ avatarBase64 }) }),

  changeEmail: (payload: { newEmail: string; password: string }) =>
    request('/api/users/me/change-email', { method: 'POST', body: JSON.stringify(payload) }),

  changePassword: (payload: { currentPassword: string; newPassword: string }) =>
    request('/api/users/me/change-password', { method: 'POST', body: JSON.stringify(payload) }),

  updateTwoFactor: (payload: { enabled: boolean; password: string }) =>
    request('/api/users/me/two-factor', { method: 'PUT', body: JSON.stringify(payload) }),

  deleteAccount: (password: string) =>
    request('/api/users/me', { method: 'DELETE', body: JSON.stringify({ password }) }),

  // ── الأجهزة النشطة (Active Sessions) ──
  mySessions: () => request('/api/users/me/sessions'),

  revokeSession: (sessionId: string) =>
    request(`/api/users/me/sessions/${sessionId}`, { method: 'DELETE' }),

  listCampaigns: () => request('/api/campaigns'),

  saveCampaign: (campaign: any, versionMeta?: { label?: string; variantKey?: 'main' | 'A' | 'B'; changeSummary?: string }) =>
    request('/api/campaigns', { method: 'POST', body: JSON.stringify({ ...campaign, _versionMeta: versionMeta }) }),

  deleteCampaign: (id: string) => request(`/api/campaigns/${id}`, { method: 'DELETE' }),

  getCampaignVersions: (id: string) => request(`/api/campaigns/${id}/versions`),

  createCampaignVersion: (id: string, payload: { label: string; variantKey?: 'main' | 'A' | 'B'; changeSummary?: string; snapshot?: any }) =>
    request(`/api/campaigns/${id}/versions`, { method: 'POST', body: JSON.stringify(payload) }),

  restoreCampaignVersion: (campaignId: string, versionId: string) =>
    request(`/api/campaigns/${campaignId}/versions/${versionId}/restore`, { method: 'POST' }),

  updateCampaignVersionPerformance: (campaignId: string, versionId: string, performance: { spend: number; reach: number; leads: number; cpl: number; impressions?: number; clicks?: number; conversions?: number; revenue?: number }) =>
    request(`/api/campaigns/${campaignId}/versions/${versionId}/performance`, { method: 'PATCH', body: JSON.stringify(performance) }),

  getBrandProfile: () => request('/api/users/me/brand-profile'),

  saveBrandProfile: (profile: any) =>
    request('/api/users/me/brand-profile', { method: 'PUT', body: JSON.stringify(profile) }),

  getCampaignReview: (campaignId: string) => request(`/api/campaigns/${campaignId}/review`),

  createCampaignReview: (campaignId: string) =>
    request(`/api/campaigns/${campaignId}/review`, { method: 'POST' }),

  updateOwnerReviewStatus: (campaignId: string, status: 'draft' | 'pending') =>
    request(`/api/campaigns/${campaignId}/review/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),

  getPublicClientReview: (token: string) => request(`/api/client-reviews/${encodeURIComponent(token)}`),

  addClientReviewComment: (token: string, payload: { author: string; section: string; text: string }) =>
    request(`/api/client-reviews/${encodeURIComponent(token)}/comments`, { method: 'POST', body: JSON.stringify(payload) }),

  updateClientReviewStatus: (token: string, status: 'approved' | 'changes_requested', approval?: { signerName: string; signerTitle?: string; signerEmail?: string; consent: boolean }) =>
    request(`/api/client-reviews/${encodeURIComponent(token)}/status`, { method: 'PATCH', body: JSON.stringify({ status, ...approval }) }),

  getOnboarding: () => request('/api/onboarding'),

  updateOnboarding: (progress: any) => request('/api/onboarding', { method: 'PATCH', body: JSON.stringify(progress) }),

  trackProductEvent: (eventName: string, campaignId?: string, metadata?: any) =>
    request('/api/product-events', { method: 'POST', body: JSON.stringify({ eventName, campaignId, metadata }) }),

  getProductAnalytics: () => request('/api/product-analytics'),

  getTeamWorkspace: () => request('/api/team-workspace'),

  getWorkspaceRole: () => request('/api/users/me/workspace-role'),

  inviteTeamMember: (payload: { name: string; email: string; role: string; usageLimit: number; dailyUsageLimit: number; permissions?: string[] }) =>
    request('/api/team-workspace/members', { method: 'POST', body: JSON.stringify(payload) }),

  updateTeamMember: (memberId: string, payload: { role: string; usageLimit: number; dailyUsageLimit: number; permissions?: string[] | null }) =>
    request(`/api/team-workspace/members/${memberId}`, { method: 'PATCH', body: JSON.stringify(payload) }),

  resendTeamInvite: (memberId: string) =>
    request(`/api/team-workspace/members/${memberId}/resend`, { method: 'POST' }),

  removeTeamMember: (memberId: string) =>
    request(`/api/team-workspace/members/${memberId}`, { method: 'DELETE' }),

  assignCampaign: (campaignId: string, memberId: string) =>
    request('/api/team-workspace/assignments', { method: 'POST', body: JSON.stringify({ campaignId, memberId }) }),

  unassignCampaign: (campaignId: string) =>
    request(`/api/team-workspace/assignments/${campaignId}`, { method: 'DELETE' }),

  addTeamComment: (text: string, campaignId?: string) =>
    request('/api/team-workspace/comments', { method: 'POST', body: JSON.stringify({ text, campaignId }) }),

  generateStrategy: (formData: any) =>
    creditRequest('/api/generate-strategy', 'campaign_generation', { method: 'POST', body: JSON.stringify(formData) }),

  getMarketInsights: (forceRefresh = false) =>
    creditRequest(`/api/market-insights${forceRefresh ? '?refresh=1' : ''}`, 'market_insights', { method: 'GET' }),

  getTranslations: () => request('/api/translations'),

  adminListUsers: () => request('/api/admin/users'),

  adminGetStats: () => request('/api/admin/stats'),

  adminGetProductAnalytics: () => request('/api/admin/product-analytics'),

  adminAddUser: (payload: any) =>
    request('/api/admin/users', { method: 'POST', body: JSON.stringify(payload) }),

  adminUpdateUser: (id: string, payload: any) =>
    request(`/api/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),

  adminDeleteUser: (id: string) => request(`/api/admin/users/${id}`, { method: 'DELETE' }),

  // ── إدارة أجهزة/جلسات المستخدمين (أدمن) ──
  adminListUserSessions: (userId: string) => request(`/api/admin/users/${userId}/sessions`),

  adminRevokeUserSession: (userId: string, sessionId: string) =>
    request(`/api/admin/users/${userId}/sessions/${sessionId}`, { method: 'DELETE' }),

  adminRevokeAllUserSessions: (userId: string) =>
    request(`/api/admin/users/${userId}/sessions`, { method: 'DELETE' }),

  adminResetUserPassword: (userId: string, newPassword?: string) =>
    request(`/api/admin/users/${userId}/reset-password`, { method: 'POST', body: JSON.stringify({ newPassword }) }),

  adminGetAuditLog: (params?: { page?: number; limit?: number; actionType?: string; adminId?: string; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.actionType) query.set('actionType', params.actionType);
    if (params?.adminId) query.set('adminId', params.adminId);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return request(`/api/admin/audit-log${qs ? `?${qs}` : ''}`);
  },

  // ── الحملات والمحادثات (أدمن) ──
  adminListCampaigns: (params?: { page?: number; limit?: number; search?: string; plan?: string; sortBy?: string }) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.search) qs.set('search', params.search);
    if (params?.plan) qs.set('plan', params.plan);
    if (params?.sortBy) qs.set('sortBy', params.sortBy);
    const query = qs.toString();
    return request(`/api/admin/campaigns${query ? `?${query}` : ''}`);
  },

  adminListUserChatSessions: (userId: string) => request(`/api/admin/users/${userId}/chat-sessions`),

  adminListChatMessages: (sessionId: string) => request(`/api/admin/chat-sessions/${sessionId}/messages`),

  // ── المدفوعات، صحة الذكاء الاصطناعي، وتحليلات النمو (أدمن) ──
  adminListPayments: () => request('/api/admin/payments'),

  adminGetAiHealth: () => request('/api/admin/ai-health'),

  adminGetConversionFunnel: () => request('/api/admin/conversion-funnel'),

  adminGetCohortRetention: () => request('/api/admin/cohort-retention'),

  adminGetPredictionAccuracy: () => request('/api/admin/prediction-accuracy'),

  rewriteCopy: (payload: { text: string; instruction: string; projectContext?: string }) =>
    creditRequest('/api/rewrite-copy', 'rewrite_copy', { method: 'POST', body: JSON.stringify(payload) }),

  regenerateStrategyPart: (payload: { projectData: any; part: 'audience' | 'budget'; currentStrategy: any; instruction?: string }) =>
    creditRequest('/api/regenerate-strategy-part', 'strategy_part_regeneration', { method: 'POST', body: JSON.stringify(payload) }),

  generateReelsScript: (payload: { projectData: any; platform?: string; duration?: string }) =>
    creditRequest('/api/generate-reels-script', 'reels_generation', { method: 'POST', body: JSON.stringify(payload) }),

  analyzeCompetitor: (payload: { competitorText?: string; competitorImageUrl?: string; projectContext?: string }) =>
    creditRequest('/api/analyze-competitor', 'competitor_analysis', { method: 'POST', body: JSON.stringify(payload) }),

  generateMultiplatform: (payload: { projectData: any; platforms?: string[] }) =>
    creditRequest('/api/generate-multiplatform', 'multiplatform_generation', { method: 'POST', body: JSON.stringify(payload) }),

  // ── الدفع (تحويل يدوي إنستاباي/محفظة إلكترونية) ──
  getPaymentPlans: () =>
    request('/api/payment/plans'),

  initiatePayment: (plan: string, discountCode?: string) =>
    request('/api/payment/initiate', { method: 'POST', body: JSON.stringify({ plan, discountCode: discountCode || undefined }) }),

  // تأكيد الضغط على "تم، سأرسل إثبات التحويل" — هنا فقط بيتسجّل طلب الدفع فعلياً
  // ويظهر في سجل المدفوعات، وبيتبعت إيميل تأكيد استلام الطلب للمستخدم.
  confirmPaymentIntent: (paymentId: string, plan: string, discountCode?: string) =>
    request('/api/payment/confirm-intent', { method: 'POST', body: JSON.stringify({ paymentId, plan, discountCode: discountCode || undefined }) }),

  validateDiscountCode: (plan: string, code: string) =>
    request('/api/payment/validate-discount', { method: 'POST', body: JSON.stringify({ plan, code }) }),

  getPaymentHistory: () =>
    request('/api/payment/history'),

  adminConfirmPayment: (paymentId: string) =>
    request(`/api/admin/payments/${paymentId}/confirm`, { method: 'POST' }),

  adminRejectPayment: (paymentId: string, reason?: string) =>
    request(`/api/admin/payments/${paymentId}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),

  // ── Admin Plan Settings ──
  adminGetPlanSettings: () =>
    request('/api/admin/plan-settings'),

  adminUpdatePlanSettings: (planKey: string, payload: { price_egp?: number; credits_granted?: number; label_ar?: string; label_en?: string; duration_days?: number; features_json?: string[]; sort_order?: number; is_active?: boolean; billing_cycle?: 'monthly' | 'annual'; base_plan_key?: string }) =>
    request(`/api/admin/plan-settings/${planKey}`, { method: 'PUT', body: JSON.stringify(payload) }),

  // ── Admin Trials & Credit Consumption ──
  adminGetCreditSystem: () => request('/api/admin/credit-system'),

  adminUpdateCreditSettings: (payload: Record<string, string | number | boolean>) =>
    request('/api/admin/credit-system/settings', { method: 'PUT', body: JSON.stringify(payload) }),

  adminUpdateCreditOperation: (operationKey: string, payload: { label_ar: string; label_en: string; cost_credits: number; is_active: boolean }) =>
    request(`/api/admin/credit-operations/${encodeURIComponent(operationKey)}`, { method: 'PUT', body: JSON.stringify(payload) }),

  adminGrantTrials: (payload: { all?: boolean; segment?: string; durationDays?: number; userIds?: string[]; assignments?: Array<{ userId: string; durationDays: number }> }) =>
    request('/api/admin/trials/grant', { method: 'POST', body: JSON.stringify(payload) }),

  adminCancelTrial: (userId: string, reason?: string) =>
    request(`/api/admin/trials/${encodeURIComponent(userId)}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),

  adminAdjustCredits: (userId: string, payload: { mode: 'add' | 'subtract' | 'set'; amount: number; reason: string }) =>
    request(`/api/admin/users/${encodeURIComponent(userId)}/credit-adjustment`, { method: 'POST', body: JSON.stringify(payload) }),

  adminGetGrowthCreditSystem: () => request('/api/admin/credit-packages'),
  adminCreateCreditPackage: (payload: any) => request('/api/admin/credit-packages', { method: 'POST', body: JSON.stringify(payload) }),
  adminUpdateCreditPackage: (id: string, payload: any) => request(`/api/admin/credit-packages/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  adminConfirmCreditPackageOrder: (id: string) => request(`/api/admin/credit-package-orders/${encodeURIComponent(id)}/confirm`, { method: 'POST' }),
  adminRejectCreditPackageOrder: (id: string) => request(`/api/admin/credit-package-orders/${encodeURIComponent(id)}/reject`, { method: 'POST' }),
  adminGetPromotionCodes: () => request('/api/admin/promotion-codes'),
  adminCreatePromotionCode: (payload: any) => request('/api/admin/promotion-codes', { method: 'POST', body: JSON.stringify(payload) }),
  adminUpdatePromotionCode: (id: string, payload: any) => request(`/api/admin/promotion-codes/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  adminGetCreditAnalytics: () => request('/api/admin/credit-analytics'),
  adminGetCreditReport: (params?: Record<string, string>) => request(`/api/admin/credit-report${params ? `?${new URLSearchParams(params).toString()}` : ''}`),
  adminGetCreditMonitoring: () => request('/api/admin/credit-monitoring'),
  adminRunCreditJob: (job: 'lifecycle' | 'reconciliation') => request(`/api/admin/credit-jobs/${job}/run`, { method: 'POST' }),

  // ── Admin Discount Codes ──
  adminGetDiscountCodes: () =>
    request('/api/admin/discount-codes'),

  adminCreateDiscountCode: (payload: { code: string; discount_type: 'percentage' | 'fixed'; discount_value: number }) =>
    request('/api/admin/discount-codes', { method: 'POST', body: JSON.stringify(payload) }),

  adminUpdateDiscountCode: (id: string, payload: { discount_type?: 'percentage' | 'fixed'; discount_value?: number; is_active?: boolean }) =>
    request(`/api/admin/discount-codes/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),

  adminDeleteDiscountCode: (id: string) =>
    request(`/api/admin/discount-codes/${id}`, { method: 'DELETE' }),

  adminSaveTranslations: (translations: Array<{ key: string; ar: string; en: string }>) =>
    request('/api/admin/translations', { method: 'PUT', body: JSON.stringify({ translations }) }),

  // ── فريق الإدارة (Admin Staff) ──
  adminListStaff: () => request('/api/admin/staff'),

  adminAddStaff: (payload: { email: string; name?: string; permissions: string[] }) =>
    request('/api/admin/staff', { method: 'POST', body: JSON.stringify(payload) }),

  adminUpdateStaff: (id: string, payload: { name?: string; permissions: string[] }) =>
    request(`/api/admin/staff/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),

  adminDeleteStaff: (id: string) => request(`/api/admin/staff/${id}`, { method: 'DELETE' }),

  // ── الرسائل الجماعية (Broadcast) ──
  adminSendBroadcast: (payload: { subject: string; message: string; segment: string }) =>
    request('/api/admin/broadcast', { method: 'POST', body: JSON.stringify(payload) }),

  adminGetBroadcastHistory: () => request('/api/admin/broadcast/history'),

  // ── صندوق وارد المنصة (info@ / noreply@) ──
  adminGetInboxStatus: () => request('/api/admin/inbox/status'),

  adminGetInboxMessages: (mailbox: 'info' | 'noreply') => request(`/api/admin/inbox/${mailbox}`),

  adminGetInboxMessage: (mailbox: 'info' | 'noreply', uid: number) =>
    request(`/api/admin/inbox/${mailbox}/${uid}`),

  // ── AI Chat ──
  listChatSessions: () => request('/api/chat/sessions'),

  createChatSession: (title: string) =>
    request('/api/chat/sessions', { method: 'POST', body: JSON.stringify({ title }) }),

  listChatMessages: (sessionId: string) =>
    request(`/api/chat/sessions/${sessionId}/messages`),

  sendChatMessage: (payload: { sessionId: string; content: string; attachmentBase64?: string | null }) =>
    creditRequest('/api/chat/messages', 'chat_message', { method: 'POST', body: JSON.stringify(payload) }),

  // ── الدعم الفني (شات بوت + موظف بشري) — العميل ──
  listSupportConversations: () => request('/api/support/conversations'),

  createSupportConversation: (payload: { subject?: string; message?: string }) =>
    request('/api/support/conversations', { method: 'POST', body: JSON.stringify(payload) }),

  getSupportMessages: (conversationId: string) =>
    request(`/api/support/conversations/${conversationId}/messages`),

  sendSupportMessage: (conversationId: string, content: string) =>
    request(`/api/support/conversations/${conversationId}/messages`, { method: 'POST', body: JSON.stringify({ content }) }),

  escalateSupportConversation: (conversationId: string) =>
    request(`/api/support/conversations/${conversationId}/escalate`, { method: 'POST' }),

  rateSupportConversation: (conversationId: string, rating: number, comment?: string) =>
    request(`/api/support/conversations/${conversationId}/rating`, { method: 'PUT', body: JSON.stringify({ rating, comment }) }),

  // ── الدعم الفني — لوحة الأدمن ──
  adminListSupportConversations: (status: string = 'all') =>
    request(`/api/admin/support/conversations?status=${encodeURIComponent(status)}`),

  adminGetSupportStats: () => request('/api/admin/support/stats'),

  adminGetSupportMessages: (conversationId: string) =>
    request(`/api/admin/support/conversations/${conversationId}/messages`),

  adminSendSupportMessage: (conversationId: string, content: string) =>
    request(`/api/admin/support/conversations/${conversationId}/messages`, { method: 'POST', body: JSON.stringify({ content }) }),

  adminUpdateSupportStatus: (conversationId: string, status: 'closed' | 'active') =>
    request(`/api/admin/support/conversations/${conversationId}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),

  // ── Site Settings (Customization) ──
  getSiteSettings: () => request('/api/site-settings'),

  adminUpdateSiteSettings: (payload: {
    primary_color?: string; accent_color?: string; font_family?: string; free_chat_monthly_limit?: string;
    marketing_content?: string; legal_content?: string; contact_page_content?: string;
    faq_content?: string; about_content?: string; seo_settings?: string;
    manual_pay_instapay_phone?: string; manual_pay_instapay_name?: string;
    manual_pay_wallet_number?: string; manual_pay_wallet_name?: string;
    vat_rate_percent?: string;
  }) =>
    request('/api/admin/site-settings', { method: 'PUT', body: JSON.stringify(payload) }),

  // ── نموذج "اتصل بنا" العام ──
  sendContactMessage: (payload: { name: string; email: string; phone?: string; inquiryType?: string; message: string }) =>
    request('/api/contact-messages', { method: 'POST', body: JSON.stringify(payload) }),

  adminGetContactMessages: () => request('/api/admin/contact-messages'),

  adminUpdateContactMessageStatus: (id: string, status: 'new' | 'read' | 'replied') =>
    request(`/api/admin/contact-messages/${id}`, { method: 'PUT', body: JSON.stringify({ status }) }),

  // ── روابط الفوتر (Footer Links) ──
  // عام: يرجّع الروابط المفعّلة بس مرتّبة، تُستخدم في فوتر الواجهة الرئيسية لأي زائر.
  getFooterLinks: () => request('/api/footer-links'),

  // أدمن: كل الروابط (مفعّلة وموقوفة) لإدارتها من لوحة المالك.
  adminGetFooterLinks: () => request('/api/admin/footer-links'),

  adminCreateFooterLink: (payload: { label_ar: string; label_en: string; url: string; open_in_new_tab?: boolean }) =>
    request('/api/admin/footer-links', { method: 'POST', body: JSON.stringify(payload) }),

  adminUpdateFooterLink: (
    id: string,
    payload: Partial<{ label_ar: string; label_en: string; url: string; open_in_new_tab: boolean; is_active: boolean; sort_order: number }>
  ) => request(`/api/admin/footer-links/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),

  adminDeleteFooterLink: (id: string) =>
    request(`/api/admin/footer-links/${id}`, { method: 'DELETE' }),

  adminReorderFooterLinks: (orderedIds: string[]) =>
    request('/api/admin/footer-links/reorder', { method: 'PUT', body: JSON.stringify({ orderedIds }) }),

  // ── شهادات العملاء (Testimonials) ──
  // عام: يرجّع الشهادات المفعّلة بس مرتّبة، تُستخدم في قسم "آراء المستخدمين" بالصفحة الرئيسية.
  getTestimonials: () => request('/api/testimonials'),

  // أدمن: كل الشهادات (مفعّلة وموقوفة) لإدارتها من لوحة المالك.
  adminGetTestimonials: () => request('/api/admin/testimonials'),

  adminCreateTestimonial: (payload: { name: string; role_ar: string; role_en: string; text_ar: string; text_en: string; company?: string; image_alt_ar?: string; image_alt_en?: string; image_url?: string | null; stars?: number }) =>
    request('/api/admin/testimonials', { method: 'POST', body: JSON.stringify(payload) }),

  adminUpdateTestimonial: (
    id: string,
    payload: Partial<{ name: string; role_ar: string; role_en: string; text_ar: string; text_en: string; company: string; image_alt_ar: string; image_alt_en: string; image_url: string | null; stars: number; is_active: boolean; sort_order: number }>
  ) => request(`/api/admin/testimonials/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),

  adminDeleteTestimonial: (id: string) =>
    request(`/api/admin/testimonials/${id}`, { method: 'DELETE' }),

  adminReorderTestimonials: (orderedIds: string[]) =>
    request('/api/admin/testimonials/reorder', { method: 'PUT', body: JSON.stringify({ orderedIds }) }),

  // ── CMS / Blog / Media / FAQ ──
  getCmsPage: (slug: string) => request(`/api/cms/pages/${encodeURIComponent(slug)}`),
  getFaqs: () => request('/api/faqs'),
  getBlogCategories: () => request('/api/blog/categories'),
  listBlogPosts: (params?: { page?: number; limit?: number; search?: string; category?: string; lang?: 'ar' | 'en' }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.search) query.set('search', params.search);
    if (params?.category) query.set('category', params.category);
    if (params?.lang) query.set('lang', params.lang);
    return request(`/api/blog${query.size ? `?${query}` : ''}`);
  },
  getBlogPost: (slug: string) => request(`/api/blog/${encodeURIComponent(slug)}`),
  adminGetPages: () => request('/api/admin/pages'),
  adminUpdatePage: (id: string, payload: any) => request(`/api/admin/pages/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(payload) }),
  adminUpdatePageSection: (id: string, payload: any) => request(`/api/admin/page-sections/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(payload) }),
  adminListMedia: (params?: { page?: number; limit?: number; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.search) query.set('search', params.search);
    return request(`/api/admin/media${query.size ? `?${query}` : ''}`);
  },
  adminUploadMedia: (file: File, fields: { alt_ar?: string; alt_en?: string; decorative?: boolean } = {}) => {
    const form = new FormData();
    form.append('file', file);
    form.append('alt_ar', fields.alt_ar || '');
    form.append('alt_en', fields.alt_en || '');
    form.append('decorative', String(Boolean(fields.decorative)));
    return request('/api/admin/media', { method: 'POST', body: form });
  },
  adminUpdateMedia: (id: string, payload: any) => request(`/api/admin/media/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(payload) }),
  adminReplaceMedia: (id: string, file: File) => {
    const form = new FormData(); form.append('file', file);
    return request(`/api/admin/media/${encodeURIComponent(id)}/file`, { method: 'PUT', body: form });
  },
  adminDeleteMedia: (id: string) => request(`/api/admin/media/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  adminListBlogPosts: (params?: { page?: number; limit?: number; search?: string; status?: string; category?: string }) => {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => { if (value !== undefined && value !== '') query.set(key, String(value)); });
    return request(`/api/admin/blog${query.size ? `?${query}` : ''}`);
  },
  adminCreateBlogPost: (payload: any) => request('/api/admin/blog', { method: 'POST', body: JSON.stringify(payload) }),
  adminUpdateBlogPost: (id: string, payload: any) => request(`/api/admin/blog/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(payload) }),
  adminDuplicateBlogPost: (id: string) => request(`/api/admin/blog/${encodeURIComponent(id)}/duplicate`, { method: 'POST' }),
  adminPreviewBlogPost: (id: string) => request(`/api/admin/blog/${encodeURIComponent(id)}/preview`),
  adminDeleteBlogPost: (id: string) => request(`/api/admin/blog/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  adminListBlogCategories: () => request('/api/admin/blog-categories'),
  adminCreateBlogCategory: (payload: any) => request('/api/admin/blog-categories', { method: 'POST', body: JSON.stringify(payload) }),
  adminUpdateBlogCategory: (id: string, payload: any) => request(`/api/admin/blog-categories/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(payload) }),
  adminDeleteBlogCategory: (id: string) => request(`/api/admin/blog-categories/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  adminListFaqs: () => request('/api/admin/faqs'),
  adminCreateFaq: (payload: any) => request('/api/admin/faqs', { method: 'POST', body: JSON.stringify(payload) }),
  adminUpdateFaq: (id: string, payload: any) => request(`/api/admin/faqs/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(payload) }),
  adminDeleteFaq: (id: string) => request(`/api/admin/faqs/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  adminReorderFaqs: (orderedIds: string[]) => request('/api/admin/faqs/reorder', { method: 'PUT', body: JSON.stringify({ orderedIds }) }),
};
